import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, storage, auth } from '../firebaseConfig';
import {
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import '../styles/StatsConfigure.css';
import { FiPlus, FiImage, FiX } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { getCurrentSeason, getAvailableSeasons, SEASONS } from '../data/seasons';
import { rateLimiter, formatTimeUntilReset } from '../utils/rateLimiter';
import { sanitizePlayerName, sanitizeTeamName, sanitizeMatchType, sanitizeRegion } from '../utils/inputSanitizer';
import { validateFile } from '../utils/fileValidation';
import { processPlayerImage } from '../utils/imageProcessing';
import {
  MATCH_TYPE_CATEGORIES,
  MATCH_TYPE_REGIONS,
  catalogToLegacyMatchTypes,
  normalizeMatchTypeCatalog,
  slugifyMatchType,
} from '../utils/matchTypeCatalog';

const StatsConfigure = ({ teamId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [players, setPlayers] = useState([]);
  const [newPlayer, setNewPlayer] = useState('');
  const [vsTeams, setVsTeams] = useState([]);
  const [newVsTeam, setNewVsTeam] = useState({ name: '', region: '' });
  const [matchTypes, setMatchTypes] = useState([]);
  const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
  const [newMatchType, setNewMatchType] = useState('');
  const [newMatchTypeCategory, setNewMatchTypeCategory] = useState('PRACTICE');
  const [newMatchTypeRegion, setNewMatchTypeRegion] = useState('GLOBAL');
  const [uploadingImages, setUploadingImages] = useState({});
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [teamName, setTeamName] = useState('');
  const [teamLogo, setTeamLogo] = useState('');
  const [teamLogoPath, setTeamLogoPath] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [seasonRegion, setSeasonRegion] = useState('');
  const guideStep = Number(new URLSearchParams(location.search).get('guideStep'));
  const isGuideMode = Number.isInteger(guideStep) && guideStep >= 1 && guideStep <= 4;

  const guideSteps = [
    { title: 'Step 1: Set Up Season', description: 'Configure season, team identity, logo, and region.' },
    { title: 'Step 2: Add Players', description: 'Add player IGN, upload card images, and drag to reorder starters/subs.' },
    { title: 'Step 3: Add Match Catalog', description: 'Separate official events from practice blocks, with regions for cleaner filters.' },
    { title: 'Step 4: Add VS Teams', description: 'Add opponent region and team names.' },
  ];

  const currentGuide = isGuideMode ? guideSteps[guideStep - 1] : null;

  const goToGuideStep = (step) => {
    navigate(`/settings?tab=database-config&guideStep=${step}`);
  };

  const showGuideStep = (step) => !isGuideMode || guideStep === step;

  const REGION_OPTIONS = [
    'North America',
    'Latin America',
    'Europe',
    'Middle East',
    'India',
    'Japan',
    'China',
    'Garena',
    'Africa',
  ];

  // Reset season selector when teamId changes (user switched teams)
  useEffect(() => {
    if (teamId) {
      setSelectedSeason(getCurrentSeason());
    }
  }, [teamId]);

  useEffect(() => {
    if (isGuideMode) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isGuideMode, guideStep]);

const saveMatchTypeCatalog = (nextCatalog) => {
  const normalizedCatalog = normalizeMatchTypeCatalog(nextCatalog, selectedSeason);
  const legacyTypes = catalogToLegacyMatchTypes(normalizedCatalog);
  setMatchTypeCatalog(normalizedCatalog);
  setMatchTypes(legacyTypes);
  saveConfig({ matchTypeCatalog: normalizedCatalog, matchTypes: legacyTypes });
};

        const handleAddMatchType = () => {
  // ✅ Sanitize match type
  const label = sanitizeMatchType(newMatchType);
  const category = MATCH_TYPE_CATEGORIES.some(option => option.value === newMatchTypeCategory)
    ? newMatchTypeCategory
    : 'PRACTICE';
  const region = sanitizeRegion(newMatchTypeRegion) || 'GLOBAL';

  if (!label) {
    toast.error('Enter a match name');
    return;
  }

  const exists = matchTypeCatalog.some(item =>
    item.label === label && item.category === category && item.region === region
  );
  if (exists) {
    toast.error('Match catalog entry already exists');
    return;
  }

  const nextEntry = {
    id: slugifyMatchType([selectedSeason, category, region, label, Date.now()]),
    label,
    category,
    region,
    season: selectedSeason,
    active: true,
  };

  saveMatchTypeCatalog([...matchTypeCatalog, nextEntry]);
  setNewMatchType('');
};

// Migrate legacy data to season structure
const migrateLegacyData = useCallback(async (data) => {
  if (!teamId) return;
  const docRef = doc(db, `teams/${teamId}/configurations/stats`);
  
  // Get existing data to preserve other seasons
  const snapshot = await getDoc(docRef);
  const existingData = snapshot.exists() ? snapshot.data() : {};
  const existingSeasons = existingData.seasons || {};
  
  await setDoc(docRef, {
    seasons: {
      ...existingSeasons,
      '2025': {
        vsTeams: data.vsTeams || [],
        matchTypes: data.matchTypes || [],
        matchTypeCatalog: normalizeMatchTypeCatalog(
          data.matchTypeCatalog || data.matchTypes || [],
          '2025'
        ),
        players: data.players || [] // Migrate players to 2025 season
      }
    }
  }, { merge: true });
}, [teamId]);

useEffect(() => {
  if (!teamId) return;
  const loadConfig = async () => {
    const docRef = doc(db, `teams/${teamId}/configurations/stats`);
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      const data = snapshot.data();
      
      // Load season-specific configs
      if (data.seasons && data.seasons[selectedSeason]) {
        const seasonConfig = data.seasons[selectedSeason];
        const normalizedCatalog = normalizeMatchTypeCatalog(
          seasonConfig.matchTypeCatalog || seasonConfig.matchTypes || [],
          selectedSeason
        );
        setVsTeams(seasonConfig.vsTeams || []);
        setMatchTypeCatalog(normalizedCatalog);
        setMatchTypes(catalogToLegacyMatchTypes(normalizedCatalog));
        setTeamName(seasonConfig.teamName || '');
        setTeamLogo(seasonConfig.teamLogo || '');
        setTeamLogoPath(seasonConfig.teamLogoPath || '');
        setSeasonRegion(seasonConfig.region || '');
        // Load season-specific players
        setPlayers((seasonConfig.players || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } else {
        // Fallback: check for legacy format (no seasons structure)
        // Migrate old data if it exists
        if (data.vsTeams || data.matchTypes) {
          const normalizedCatalog = normalizeMatchTypeCatalog(
            data.matchTypeCatalog || data.matchTypes || [],
            selectedSeason
          );
          setVsTeams(data.vsTeams || []);
          setMatchTypeCatalog(normalizedCatalog);
          setMatchTypes(catalogToLegacyMatchTypes(normalizedCatalog));
          // Migrate players to 2025 season if legacy data exists
          if (selectedSeason === '2025' && data.players) {
            setPlayers((data.players || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
            // Optionally migrate to new structure
            await migrateLegacyData(data);
          } else {
            setPlayers([]);
          }
        } else {
          setVsTeams([]);
          setMatchTypes([]);
          setMatchTypeCatalog([]);
          setPlayers([]);
        }
        setTeamName('');
        setTeamLogo('');
        setTeamLogoPath('');
        setSeasonRegion('');
      }
    }
  };
  loadConfig();
}, [teamId, selectedSeason, migrateLegacyData]);

const saveConfig = async (updated = {}) => {
  if (!teamId) return;
  
  // Check rate limit (but allow more frequent saves for config since it's auto-save)
  const check = rateLimiter.checkLimit('SAVE_CONFIG');
  if (!check.allowed) {
    // Don't show error for config saves - just silently skip (it's auto-save)
    console.warn('Config save rate limited');
    return;
  }

  // Record attempt (but don't block if it fails - config saves are frequent)
  rateLimiter.record('SAVE_CONFIG');
  
  const docRef = doc(db, `teams/${teamId}/configurations/stats`);
  
  // Get existing data first
  const snapshot = await getDoc(docRef);
  const existingData = snapshot.exists() ? snapshot.data() : {};
  
  const nextCatalog = updated.matchTypeCatalog !== undefined
    ? normalizeMatchTypeCatalog(updated.matchTypeCatalog, selectedSeason)
    : matchTypeCatalog;
  const nextMatchTypes = updated.matchTypes !== undefined
    ? updated.matchTypes
    : catalogToLegacyMatchTypes(nextCatalog);

  // Build season-specific config (including players)
  const seasonConfig = {
    vsTeams: updated.vsTeams !== undefined ? updated.vsTeams : vsTeams,
    matchTypes: nextMatchTypes,
    matchTypeCatalog: nextCatalog,
    teamName: updated.teamName !== undefined ? updated.teamName : teamName,
    teamLogo: updated.teamLogo !== undefined ? updated.teamLogo : teamLogo,
    teamLogoPath: updated.teamLogoPath !== undefined ? updated.teamLogoPath : teamLogoPath,
    region: updated.region !== undefined ? updated.region : seasonRegion,
    players: updated.players !== undefined ? updated.players : players
  };
  
  // Preserve other seasons' data
  const existingSeasons = existingData.seasons || {};
  
  await setDoc(docRef, {
    seasons: {
      ...existingSeasons,
      [selectedSeason]: seasonConfig
    },
    ...updated
  }, { merge: true });

  // Mirror identity into season doc so Join Team can read it
  try {
    await setDoc(
      doc(db, `teams/${teamId}/seasons/${selectedSeason}`),
      {
        region: seasonConfig.region || '',
        displayName: seasonConfig.teamName || '',
        logo: seasonConfig.teamLogo || '',
        year: selectedSeason,
        teamId,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );
  } catch (err) {
    console.error('Failed to update season doc for identity/region', err);
  }
};

 const handleAddPlayer = async () => {
  // ✅ Sanitize player name
  const sanitizedIgn = sanitizePlayerName(newPlayer);
  if (!sanitizedIgn || players.some(p => p.ign === sanitizedIgn.toUpperCase())) {
    if (players.some(p => p.ign === sanitizedIgn.toUpperCase())) {
      toast.error('Player already exists');
    } else if (!sanitizedIgn) {
      toast.error('Invalid player name');
    }
    return;
  }

  const updated = [...players, {
    ign: sanitizedIgn.toUpperCase(),
    isActive: true,
    order: players.length,
    addedAt: new Date().toISOString(),
    imageUrl: '',
    imagePath: '',
  }];
  setPlayers(updated);
  setNewPlayer('');
  await saveConfig({ players: updated });
  toast.success('Player added successfully');
};

const removeItem = async (type, index) => {
  if (type === 'players') {
    const player = players[index];
    
    // Delete image from storage if it exists
    if (player.imagePath) {
      try {
        const imageRef = ref(storage, player.imagePath);
        await deleteObject(imageRef);
      } catch (err) {
        console.error('Failed to delete image from storage:', err);
        // Continue with player removal even if image deletion fails
      }
    }
    
    // Remove player from config
    const updated = players
      .filter((_, i) => i !== index)
      .map((p, i) => ({ ...p, order: i })); // Recalculate order
    setPlayers(updated);
    await saveConfig({ players: updated });
    toast.success('Player removed');
  } else if (type === 'vsTeams') {
    const updated = [...vsTeams];
    updated.splice(index, 1);
    setVsTeams(updated);
    saveConfig({ vsTeams: updated });
  } else if (type === 'matchTypes') {
    const updated = [...matchTypes];
    updated.splice(index, 1);
    setMatchTypes(updated);
    saveConfig({ matchTypes: updated });
  } else if (type === 'matchTypeCatalog') {
    const updated = [...matchTypeCatalog];
    updated.splice(index, 1);
    saveMatchTypeCatalog(updated);
  }
};

const toggleMatchTypeActive = (id) => {
  const updated = matchTypeCatalog.map(item =>
    item.id === id ? { ...item, active: item.active === false } : item
  );
  saveMatchTypeCatalog(updated);
};

const updateMatchTypeCatalogEntry = (id, changes) => {
  const target = matchTypeCatalog.find(item => item.id === id);
  if (!target) return;

  const normalizedChanges = { ...changes };
  if (normalizedChanges.category) {
    normalizedChanges.category = MATCH_TYPE_CATEGORIES.some(option => option.value === normalizedChanges.category)
      ? normalizedChanges.category
      : target.category;
  }
  if (normalizedChanges.region) {
    normalizedChanges.region = sanitizeRegion(normalizedChanges.region) || target.region || 'GLOBAL';
  }

  const nextEntry = { ...target, ...normalizedChanges };
  const duplicate = matchTypeCatalog.some(item =>
    item.id !== id &&
    item.label === nextEntry.label &&
    item.category === nextEntry.category &&
    item.region === nextEntry.region
  );

  if (duplicate) {
    toast.error('Match catalog entry already exists in that category and region');
    return;
  }

  const updated = matchTypeCatalog.map(item =>
    item.id === id ? nextEntry : item
  );
  saveMatchTypeCatalog(updated);
};

  const handleAddVsTeam = () => {
    // ✅ Sanitize team name and region
    const sanitizedName = sanitizeTeamName(newVsTeam.name);
    const sanitizedRegion = sanitizeRegion(newVsTeam.region);
    if (!sanitizedName || !sanitizedRegion) return;
    const updated = [...vsTeams, { name: sanitizedName.toUpperCase(), region: sanitizedRegion.toUpperCase() }];
    setVsTeams(updated);
    setNewVsTeam({ name: '', region: '' });
    saveConfig({ vsTeams: updated });
  };

const savePlayerOrder = async () => {
  const reordered = players.map((p, i) => ({ ...p, order: i }));
  setPlayers(reordered);
  await saveConfig({ players: reordered });
  toast.success("✅ Player lineup order saved.");
};

  const handleImageUpload = async (playerIndex, file) => {
    if (!file) return;
    
    const player = players[playerIndex];
    if (!player) return;

    // Validate type/content first, then enforce size on processed output.
    const inputValidation = await validateFile(file, {
      type: 'playerPicture',
      requireImageContent: true,
      skipSizeCheck: true,
    });
    if (!inputValidation.isValid) {
      toast.error(`Invalid file: ${inputValidation.errors.join(', ')}`);
      return;
    }

    let processedFile;
    try {
      processedFile = await processPlayerImage(file, {
        maxWidth: 512,
        maxHeight: 512,
        outputType: 'image/webp',
        quality: 0.82,
        maxOutputBytes: 3 * 1024 * 1024,
      });
    } catch (err) {
      console.error('Failed to process player image:', err);
      toast.error('Failed to process player picture. Try a different image.');
      return;
    }

    const outputValidation = await validateFile(processedFile, {
      type: 'playerPicture',
      requireImageContent: false,
    });
    if (!outputValidation.isValid) {
      toast.error(`Invalid file: ${outputValidation.errors.join(', ')}`);
      return;
    }

    console.log('Uploading image for player:', player.ign);
    console.log('Team ID:', teamId);
    console.log('Current User:', auth.currentUser?.uid);

    setUploadingImages(prev => ({ ...prev, [playerIndex]: true }));

    try {
      // Delete old image if it exists
    if (player.imagePath) {
      try {
        const oldImageRef = ref(storage, player.imagePath);
        await deleteObject(oldImageRef);
      } catch (err) {
        console.error('Failed to delete old image:', err);
        // Continue with upload
      }
    }

    // Upload new image
    const fileExt = processedFile.name.split('.').pop() || 'webp';
    const fileName = `${player.ign.replace(/\s+/g, '-')}-${Date.now()}.${fileExt}`;
    const storagePath = `my-team/${teamId}/player-pictures/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, processedFile, { contentType: processedFile.type });
    const imageUrl = await getDownloadURL(storageRef);

    // Update player with new image
    const updated = [...players];
    updated[playerIndex] = {
      ...updated[playerIndex],
      imageUrl,
      imagePath: storagePath,
    };
    
    setPlayers(updated);
    await saveConfig({ players: updated });
    toast.success('Player picture uploaded successfully');
  } catch (err) {
    console.error('Failed to upload image:', err);
    toast.error('Failed to upload player picture');
  } finally {
    setUploadingImages(prev => ({ ...prev, [playerIndex]: false }));
  }
};

const handleLogoUpload = async (file) => {
  if (!file) return;
  
  if (!teamId) {
    toast.error('Team ID not found');
    return;
  }

  // Check rate limit
  const check = rateLimiter.checkLimit('UPLOAD_LOGO');
  if (!check.allowed) {
    const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('UPLOAD_LOGO'));
    toast.error(`Upload rate limit exceeded. Please try again in ${timeUntilReset}.`);
    return;
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    toast.error('Please upload an image file');
    return;
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('Image size must be less than 5MB');
    return;
  }

  if (!rateLimiter.record('UPLOAD_LOGO')) {
    const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('UPLOAD_LOGO'));
    toast.error(`Upload rate limit exceeded. Please try again in ${timeUntilReset}.`);
    return;
  }

  setUploadingLogo(true);

  try {
    // Delete old logo if path exists
    if (teamLogoPath) {
      try {
        const oldLogoRef = ref(storage, teamLogoPath);
        await deleteObject(oldLogoRef);
      } catch (err) {
        console.error('Failed to delete old logo:', err);
        // Continue with upload
      }
    }

    // Upload new logo
    const fileExt = file.name.split('.').pop() || 'png';
    const fileName = `team-logo-${selectedSeason}-${Date.now()}.${fileExt}`;
    const storagePath = `my-team/${teamId}/team-logos/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, file);
    const logoUrl = await getDownloadURL(storageRef);

    // Update state and save (store both URL and path for deletion)
    setTeamLogo(logoUrl);
    setTeamLogoPath(storagePath);
    await saveConfig({ teamLogo: logoUrl, teamLogoPath: storagePath });
    toast.success('Team logo uploaded successfully');
  } catch (err) {
    console.error('Failed to upload logo:', err);
    toast.error('Failed to upload team logo');
  } finally {
    setUploadingLogo(false);
  }
};

  return (
    <div className="stats-configure-wrapper">
      {isGuideMode && currentGuide && (
        <div className="config-card" style={{ marginBottom: '24px', border: '1px solid #b085e2' }}>
          <h3 style={{ marginBottom: '8px' }}>{currentGuide.title}</h3>
          <p style={{ color: '#b7b7c9', marginBottom: '12px' }}>{currentGuide.description}</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="config-button"
              onClick={() => goToGuideStep(guideStep - 1)}
              disabled={guideStep === 1}
            >
              Previous Step
            </button>
            <button
              type="button"
              className="config-button"
              onClick={() => goToGuideStep(guideStep + 1)}
              disabled={guideStep === guideSteps.length}
            >
              Next Step
            </button>
            <button
              type="button"
              className="config-button"
              onClick={() => navigate('/community')}
            >
              Exit Guide
            </button>
          </div>
        </div>
      )}
      <h2 style={{ marginBottom: '24px' }}>
        Stats Configuration — <span style={{ color: '#b085e2' }}>{teamId}</span>
      </h2>

      {/* Season Selector */}
      {showGuideStep(1) && (
      <div className="config-card" style={{ marginBottom: '24px' }}>
        <h3>Select Season</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #444',
              background: '#2a2a2a',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '200px'
            }}
          >
            {getAvailableSeasons().map(seasonKey => (
              <option key={seasonKey} value={seasonKey}>
                {SEASONS[seasonKey].name} {SEASONS[seasonKey].isActive ? '(Current)' : ''}
              </option>
            ))}
          </select>
          <span style={{ color: '#aaa', fontSize: '14px' }}>
            Configuring {SEASONS[selectedSeason]?.name || selectedSeason} settings
          </span>
        </div>
      </div>
      )}

      {/* Team Name & Logo Configuration */}
      {showGuideStep(1) && (
      <div className="config-card" style={{ marginBottom: '24px' }}>
        <h3>Team Identity - {SEASONS[selectedSeason]?.name || selectedSeason}</h3>
        <p style={{ color: '#aaa', fontSize: '12px', marginBottom: '16px' }}>
          Configure your team name and logo for this season. This won't affect other seasons.
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
              Team Name
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={teamName}
                onChange={e => setTeamName(e.target.value.toUpperCase())}
                placeholder="Enter team name for this season"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  background: '#2a2a2a',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                className="config-button"
                onClick={() => saveConfig({ teamName })}
                disabled={!teamName.trim()}
              >
                Save Name
              </button>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
              Team Logo
            </label>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {teamLogo && (
                <img
                  src={teamLogo}
                  alt="Team Logo"
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'contain',
                    border: '2px solid #444',
                    borderRadius: '6px',
                    background: '#1a1a1a'
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={teamLogo}
                    onChange={e => setTeamLogo(e.target.value)}
                    placeholder="Enter logo URL"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #444',
                      background: '#2a2a2a',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <label
                    className="config-button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                      opacity: uploadingLogo ? 0.6 : 1
                    }}
                  >
                    <FiImage size={16} />
                    {uploadingLogo ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          await handleLogoUpload(file);
                        }
                        e.target.value = '';
                      }}
                      disabled={uploadingLogo}
                    />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="config-button"
                    onClick={() => saveConfig({ teamLogo })}
                    disabled={!teamLogo.trim() || uploadingLogo}
                  >
                    Save Logo
                  </button>
                  {teamLogo && (
                    <button
                      type="button"
                      className="config-button"
                      onClick={async () => {
                        // Delete from storage if path exists
                        if (teamLogoPath) {
                          try {
                            const logoRef = ref(storage, teamLogoPath);
                            await deleteObject(logoRef);
                          } catch (err) {
                            console.error('Failed to delete logo from storage:', err);
                            // Continue with removal even if deletion fails
                          }
                        }
                        setTeamLogo('');
                        setTeamLogoPath('');
                        await saveConfig({ teamLogo: '', teamLogoPath: '' });
                        toast.success('Logo removed');
                      }}
                      style={{ background: '#d32f2f' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
            <p style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
              Tip: Upload an image file or enter a logo URL
            </p>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
              Region
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={seasonRegion}
                onChange={(e) => setSeasonRegion(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  background: '#2a2a2a',
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                <option value="">Select region</option>
                {REGION_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                type="button"
                className="config-button"
                onClick={() => saveConfig({ region: seasonRegion })}
                disabled={!seasonRegion}
              >
                Save Region
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Active Players Section */}
      {showGuideStep(2) && (
      <div className="config-card">
        <h3>Active Players - {SEASONS[selectedSeason]?.name || selectedSeason}</h3>
        <p style={{ color: '#aaa', fontSize: '12px', marginBottom: '16px' }}>
          Configure active players for this season. Players added here will only appear in {SEASONS[selectedSeason]?.name || selectedSeason} data views.
        </p>
       <div className="config-input-group">
  <input
    style={{ textTransform: 'uppercase' }}
    value={newPlayer}
    onChange={e => setNewPlayer(e.target.value.toUpperCase())}
    placeholder="Add Player IGN"
  />
  <button className="config-button" onClick={handleAddPlayer}>
    <FiPlus />
  </button>
</div>

<DragDropContext
  onDragEnd={(result) => {
    if (!result.destination) return;
    const reordered = Array.from(players);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setPlayers(reordered);
  }}
>
  <Droppable droppableId="players" direction="horizontal">
    {(provided) => (
      <div
        className="player-grid"
        ref={provided.innerRef}
        {...provided.droppableProps}
      >
        {players.map((p, i) => (
          <Draggable key={p.ign} draggableId={p.ign} index={i}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className={`player-card ${i < 5 ? 'starter' : 'substitute'}`}
                style={{ ...provided.draggableProps.style }}
              >
                <button
                  className="remove-player-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem('players', i);
                  }}
                  title="Remove Player"
                >
                  <FiX size={16} />
                </button>

                <label className="player-card-image-wrapper">
                  {p.imageUrl ? (
                    <img 
                      src={p.imageUrl} 
                      alt={p.ign} 
                      className="player-card-image"
                    />
                  ) : (
                    <div className="player-card-placeholder">
                      <FiImage size={24} />
                    </div>
                  )}
                  
                  <div className="player-card-overlay">
                    {uploadingImages[i] ? (
                      <span style={{ fontSize: '10px' }}>Uploading...</span>
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImageUpload(i, file);
                      }
                      e.target.value = '';
                    }}
                    disabled={uploadingImages[i]}
                  />
                </label>

                <div className="player-card-name">{p.ign}</div>
                <div className="player-role-badge">
                  {i < 5 ? 'Starter' : 'Substitute'}
                </div>
              </div>
            )}
          </Draggable>
        ))}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
</DragDropContext>

        <button className="config-button" onClick={savePlayerOrder} style={{ marginTop: '12px' }}>
          Save Lineup Order
        </button>
      </div>
      )}

       {/* Match Catalog Section */}
    {showGuideStep(3) && (
    <div className="config-card">
      <h3>Match Catalog</h3>
      <p className="config-description">
        Organize official tournaments and practice blocks by region. Existing stat forms still receive active labels for now.
      </p>
      <div className="match-catalog-input-grid">
        <select
          value={newMatchTypeCategory}
          onChange={e => setNewMatchTypeCategory(e.target.value)}
        >
          {MATCH_TYPE_CATEGORIES.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={newMatchTypeRegion}
          onChange={e => setNewMatchTypeRegion(e.target.value)}
        >
          {MATCH_TYPE_REGIONS.map(region => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>
        <input
          style={{ textTransform: 'uppercase' }}
          value={newMatchType}
          onChange={e => setNewMatchType(e.target.value.toUpperCase())}
          placeholder={newMatchTypeCategory === 'OFFICIAL' ? 'NA POINTS MAJOR 2026' : 'SCRIMMAGE MXRVEL'}
        />
        <button className="config-button" onClick={handleAddMatchType}>
          <FiPlus />
        </button>
      </div>
      <div className="match-catalog-summary">
        <span>{matchTypeCatalog.length} catalog entries</span>
        <span>{matchTypes.length} active form options</span>
      </div>

      <div className="match-catalog-groups">
        {MATCH_TYPE_CATEGORIES.map(category => {
          const entries = matchTypeCatalog.filter(item => item.category === category.value);
          return (
            <div key={category.value} className="match-catalog-group">
              <div className="match-catalog-group-header">
                <h4>{category.label}</h4>
                <span>{entries.length}</span>
              </div>
              {entries.length === 0 ? (
                <div className="match-catalog-empty">No entries yet</div>
              ) : (
                <div className="match-catalog-list">
                  {entries.map(entry => {
                    const sourceIndex = matchTypeCatalog.findIndex(item => item.id === entry.id);
                    return (
                      <div
                        key={entry.id}
                        className={`match-catalog-row ${entry.active === false ? 'is-inactive' : ''}`}
                      >
                        <div className="match-catalog-info">
                          <strong>{entry.label}</strong>
                          <span>{entry.region || 'GLOBAL'} · {entry.season || selectedSeason}</span>
                        </div>
                        <div className="match-catalog-controls">
                          <div className="match-catalog-edit-grid">
                            <label className="match-catalog-field-edit">
                              <span>Category</span>
                              <select
                                value={entry.category || 'PRACTICE'}
                                onChange={(e) => updateMatchTypeCatalogEntry(entry.id, { category: e.target.value })}
                              >
                                {MATCH_TYPE_CATEGORIES.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </label>
                            <label className="match-catalog-field-edit">
                              <span>Region</span>
                              <select
                                value={entry.region || 'GLOBAL'}
                                onChange={(e) => updateMatchTypeCatalogEntry(entry.id, { region: e.target.value })}
                              >
                                {MATCH_TYPE_REGIONS.map(region => (
                                  <option key={region} value={region}>{region}</option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <div className="match-catalog-actions">
                            <button
                              type="button"
                              className="match-catalog-toggle"
                              onClick={() => toggleMatchTypeActive(entry.id)}
                            >
                              {entry.active === false ? 'Inactive' : 'Active'}
                            </button>
                            <button
                              type="button"
                              className="match-catalog-remove"
                              onClick={() => removeItem('matchTypeCatalog', sourceIndex)}
                              aria-label={`Remove ${entry.label}`}
                            >
                              <FiX />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    )}



      {/* VS Teams Section */}
      {showGuideStep(4) && (
      <div className="config-card">
        <h3>VS Teams</h3>
        <div className="config-input-group" style={{ flexWrap: 'wrap' }}>
          <select
            style={{ textTransform: 'uppercase' }}
            value={newVsTeam.region}
            onChange={e =>
              setNewVsTeam(prev => ({ ...prev, region: e.target.value.toUpperCase() }))
            }
          >
            <option value="">Select Region</option>
            {[
              'AFRICA',
              'CHINA',
              'EUROPE',
              'GARENA',
              'INDIA',
              'JAPAN',
              'LATAM',
              'NORTH_AMERICA',
            ].sort().map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>

          <input
            style={{ textTransform: 'uppercase' }}
            value={newVsTeam.name}
            onChange={e =>
              setNewVsTeam(prev => ({ ...prev, name: e.target.value.toUpperCase() }))
            }
            placeholder="VS Team Name"
          />

          <button className="config-button" onClick={handleAddVsTeam}>
            <FiPlus />
          </button>
        </div>

        <div className="config-tags">
          {vsTeams.map((vs, i) => (
            <span key={i} className="config-tag">
              {vs.name} ({vs.region})
              <button onClick={() => removeItem('vsTeams', i)}>×</button>
            </span>
          ))}
        </div>
      </div>
      )}

      {/* Debug Info */}
      <div style={{ marginTop: '20px', padding: '10px', background: '#333', borderRadius: '5px', fontSize: '12px' }}>
        <p><strong>Debug Info:</strong></p>
        <p>Team ID: {teamId}</p>
        <p>User UID: {auth.currentUser?.uid}</p>
      </div>
    </div>
  );
};

export default StatsConfigure;
